/* ══════════════════════════════════════════════════════════════
   World Monitor — Global OSINT Command Center Page
   Full-screen map + sidebars: Live TV, News Wire, Financial,
   Polymarket, DEFCON
   ══════════════════════════════════════════════════════════════ */

import { renderGDELTMap, getGDELTThemes, setGDELTTheme, destroyGDELTMap } from '../components/GDELTMap';
import { renderWorldLiveTV } from '../components/WorldLiveTV';
import { renderFinancialTracker, destroyFinancialTracker } from '../components/FinancialTracker';
import { renderPredictionMarkets, destroyPredictionMarkets } from '../components/PredictionMarkets';
import { renderDEFCONTracker, destroyDEFCONTracker } from '../components/DEFCONTracker';
import { fetchNews, type NewsItem } from '../services/news-feed';
import { renderPhase2WorldMonitorEngine } from '../components/charts/Phase2WorldMonitorEngine';
import { isFeatureEnabled } from '../services/runtime-config';

type ViewMode = 'map' | 'dashboard';
type WorldTheme = 'all' | 'conflict' | 'diplomacy' | 'economy' | 'disaster' | 'health' | 'protest';
type HeaderRisk = 'low' | 'elevated' | 'high' | 'critical';

interface WorldHeadline {
  title: string;
  source: string;
  timeAgo: string;
  pubDate?: string;
  link: string;
  tag: string;
  sentiment: 'up' | 'down' | 'neutral';
}

const cleanupFns: Array<() => void> = [];
const THEME_KEYWORDS: Record<Exclude<WorldTheme, 'all'>, string[]> = {
  conflict: ['war', 'military', 'missile', 'attack', 'troops', 'weapon', 'border', 'strike', 'navy', 'army'],
  diplomacy: ['summit', 'treaty', 'talks', 'minister', 'ambassador', 'peace', 'deal', 'dialogue', 'bilateral'],
  economy: ['market', 'stocks', 'gdp', 'inflation', 'trade', 'rates', 'bank', 'tariff', 'currency', 'oil'],
  disaster: ['earthquake', 'flood', 'storm', 'wildfire', 'hurricane', 'tsunami', 'disaster', 'volcano'],
  health: ['health', 'covid', 'vaccine', 'disease', 'hospital', 'virus', 'who', 'outbreak'],
  protest: ['protest', 'strike', 'riot', 'uprising', 'demonstration', 'rally', 'clash'],
};
const THEME_TAG: Record<Exclude<WorldTheme, 'all'>, string> = {
  conflict: 'CONFLICT',
  diplomacy: 'DIPLOMACY',
  economy: 'ECONOMY',
  disaster: 'DISASTER',
  health: 'HEALTH',
  protest: 'PROTEST',
};

let currentView: ViewMode = 'map';
let currentTheme: WorldTheme = 'all';
let cachedNews: NewsItem[] = [];
let newsRequestId = 0;

function getCurrentTimestamp(): string {
  const now = new Date();
  return now.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

function buildFilterSidebar(): string {
  const themes = getGDELTThemes();
  const icons: Record<string, string> = {
    all: '🌐',
    conflict: '⚔️',
    diplomacy: '🤝',
    economy: '💹',
    disaster: '🌊',
    health: '🏥',
    protest: '✊',
  };

  return Object.entries(themes).map(([key, label]) => `
    <button class="wm-sidebar__btn ${key === 'all' ? 'wm-sidebar__btn--active' : ''}" data-theme="${key}" id="wm-filter-${key}">
      ${icons[key] || '📰'}
      <span class="wm-sidebar__btn-tooltip">${label}</span>
    </button>
  `).join('');
}

function escapeHtml(value?: string): string {
  if (!value) return '';
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function buildNewsTicker(headlines: string[]): string {
  const items = headlines.map(item => `
    <span class="wm-ticker__item">${escapeHtml(item)}</span>
    <span class="wm-ticker__separator">•</span>
  `).join('');

  const fallback = `
    <span class="wm-ticker__item">LIVE GLOBAL FEED UNAVAILABLE</span>
    <span class="wm-ticker__separator">•</span>
  `;

  return headlines.length > 0 ? `${items}${items}` : `${fallback}${fallback}`;
}

function normalizeTheme(theme: string): WorldTheme {
  if (
    theme === 'all' ||
    theme === 'conflict' ||
    theme === 'diplomacy' ||
    theme === 'economy' ||
    theme === 'disaster' ||
    theme === 'health' ||
    theme === 'protest'
  ) {
    return theme;
  }
  return 'all';
}

function toTimestamp(pubDate?: string): number {
  if (!pubDate) return 0;
  const ts = new Date(pubDate).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

function deriveSentiment(item: NewsItem): 'up' | 'down' | 'neutral' {
  const fromApi = (item.sentiment || '').toLowerCase();
  if (fromApi.includes('positive') || fromApi.includes('bull')) return 'up';
  if (fromApi.includes('negative') || fromApi.includes('bear')) return 'down';

  const text = `${item.title} ${item.description}`.toLowerCase();
  if (/(surge|rally|gains|deal signed|expands|growth|record high|ceasefire)/.test(text)) return 'up';
  if (/(attack|war|falls|drop|clash|crash|cuts|outbreak|concern|sanction|missile)/.test(text)) return 'down';
  return 'neutral';
}

function scoreThemeRelevance(item: NewsItem, theme: WorldTheme): number {
  if (theme === 'all') return 1;
  const text = `${item.title} ${item.description} ${item.category}`.toLowerCase();
  const words = THEME_KEYWORDS[theme];
  return words.reduce((score, token) => score + (text.includes(token) ? 1 : 0), 0);
}

function toHeadline(item: NewsItem, theme: WorldTheme): WorldHeadline {
  const categoryTag = (item.category || 'world').slice(0, 10).toUpperCase();
  return {
    title: item.title || 'Untitled alert',
    source: item.source || 'Newswire',
    timeAgo: item.timeAgo || 'recent',
    pubDate: item.pubDate,
    link: /^https?:\/\//i.test(item.link || '') ? item.link : '#',
    tag: theme === 'all' ? categoryTag : THEME_TAG[theme],
    sentiment: deriveSentiment(item),
  };
}

function dedupeByTitleLatest(articles: NewsItem[]): NewsItem[] {
  const unique = new Map<string, NewsItem>();
  for (const article of articles) {
    const key = article.title.trim().toLowerCase();
    if (!key) continue;
    const prev = unique.get(key);
    if (!prev || toTimestamp(article.pubDate) > toTimestamp(prev.pubDate)) {
      unique.set(key, article);
    }
  }
  return Array.from(unique.values()).sort((a, b) => toTimestamp(b.pubDate) - toTimestamp(a.pubDate));
}

function pickLatestWorldHeadlines(theme: WorldTheme, articles: NewsItem[]): WorldHeadline[] {
  const deduped = dedupeByTitleLatest(articles);

  if (theme === 'all') {
    return deduped.slice(0, 24).map(item => toHeadline(item, theme));
  }

  const relevant = deduped
    .map(item => ({ item, relevance: scoreThemeRelevance(item, theme) }))
    .filter(row => row.relevance > 0)
    .sort((a, b) => {
      const timeDiff = toTimestamp(b.item.pubDate) - toTimestamp(a.item.pubDate);
      if (timeDiff !== 0) return timeDiff;
      return b.relevance - a.relevance;
    })
    .map(row => row.item);

  const relevantTitles = new Set(relevant.map(r => r.title));
  const latestGeneral = deduped.filter(item => !relevantTitles.has(item.title));
  const selected = [...relevant.slice(0, 16), ...latestGeneral.slice(0, 12)].slice(0, 24);
  return selected.map(item => toHeadline(item, theme));
}

function computeRiskLevel(headlines: WorldHeadline[]): HeaderRisk {
  const riskScore = headlines.reduce((score, item) => {
    if (item.sentiment === 'down') return score + 2;
    if (item.sentiment === 'neutral') return score + 1;
    return score;
  }, 0);

  if (riskScore >= 30) return 'critical';
  if (riskScore >= 24) return 'high';
  if (riskScore >= 16) return 'elevated';
  return 'low';
}

function renderThreatMatrix(headlines: WorldHeadline[]): void {
  const signal = document.getElementById('wm-signal');
  if (!signal) return;

  signal.classList.remove(
    'wm-header__status--low',
    'wm-header__status--elevated',
    'wm-header__status--high',
    'wm-header__status--critical'
  );

  const risk = computeRiskLevel(headlines);
  const label: Record<HeaderRisk, string> = {
    low: 'LOW',
    elevated: 'ELEVATED',
    high: 'HIGH',
    critical: 'CRITICAL',
  };
  signal.classList.add(`wm-header__status--${risk}`);
  signal.textContent = `THREAT MATRIX: ${label[risk]}`;
}

function updateTicker(headlines: WorldHeadline[]): void {
  const ticker = document.getElementById('wm-ticker-content');
  if (!ticker) return;
  const items = headlines.slice(0, 20).map(item => `[${item.tag}] ${item.title.toUpperCase()}`);
  ticker.innerHTML = buildNewsTicker(items);
}

function renderWorldNewsPanel(theme: WorldTheme, headlines: WorldHeadline[]): void {
  const newsBody = document.getElementById('wm-news-body');
  if (!newsBody) return;

  const downCount = headlines.filter(item => item.sentiment === 'down').length;
  const upCount = headlines.filter(item => item.sentiment === 'up').length;
  const neutralCount = headlines.length - downCount - upCount;
  const themeLabel = theme === 'all' ? 'ALL THEMES' : THEME_TAG[theme];

  const rows = headlines.map(item => `
    <a class="wm-world-news-item wm-world-news-item--${item.sentiment}" href="${escapeHtml(item.link)}" target="_blank" rel="noreferrer noopener">
      <span class="wm-world-news-item__tag">${escapeHtml(item.tag)}</span>
      <span class="wm-world-news-item__title">${escapeHtml(item.title)}</span>
      <span class="wm-world-news-item__meta">${escapeHtml(item.source)} • ${escapeHtml(item.timeAgo)}</span>
    </a>
  `).join('');

  newsBody.innerHTML = `
    <div class="wm-news-summary">
      <span class="wm-news-summary__chip">${themeLabel}</span>
      <span class="wm-news-summary__chip wm-news-summary__chip--down">Risk ${downCount}</span>
      <span class="wm-news-summary__chip wm-news-summary__chip--up">Bull ${upCount}</span>
      <span class="wm-news-summary__chip wm-news-summary__chip--neutral">Neutral ${neutralCount}</span>
    </div>
    <div class="wm-world-news-list">
      ${rows || '<div class="wm-empty">No news available</div>'}
    </div>
  `;

  renderThreatMatrix(headlines);
  updateTicker(headlines);
}

async function loadWorldNews(force: boolean = false): Promise<void> {
  const newsBody = document.getElementById('wm-news-body');
  if (!newsBody) return;

  if (force || cachedNews.length === 0) {
    newsBody.innerHTML = `
      <div class="wm-loading">
        <div class="wm-loading__spinner"></div>
        Pulling latest global wire...
      </div>
    `;
  }

  const requestId = ++newsRequestId;
  let allNews = cachedNews;

  try {
    const batches = await Promise.allSettled([
      fetchNews({ category: 'business', size: 10, maxFeeds: 16, cacheTTL: 45 }),
      fetchNews({ category: 'politics', size: 10, maxFeeds: 16, cacheTTL: 45 }),
      fetchNews({ category: 'top', size: 10, maxFeeds: 16, cacheTTL: 45 }),
    ]);

    const merged: NewsItem[] = [];
    batches.forEach(result => {
      if (result.status === 'fulfilled') merged.push(...result.value);
    });

    if (merged.length > 0) {
      allNews = dedupeByTitleLatest(merged);
      cachedNews = allNews;
    }

    if (requestId !== newsRequestId) return;
    const selected = pickLatestWorldHeadlines(currentTheme, allNews);
    renderWorldNewsPanel(currentTheme, selected);
  } catch {
    if (requestId !== newsRequestId) return;
    const selected = pickLatestWorldHeadlines(currentTheme, allNews);
    renderWorldNewsPanel(currentTheme, selected);
  }
}

/**
 * Render the World Monitor page HTML shell
 */
export function renderWorldMonitor(): string {
  if (isFeatureEnabled('charts_v2_engine')) {
    return '<div id="world-monitor-v2-root"></div>';
  }

  return `
    <div class="wm-page">
      <!-- Header Bar -->
      <div class="wm-header">
        <div class="wm-header__brand">
          <span class="wm-header__brand-dot"></span>
          WORLD MONITOR<span class="wm-header__beta">BETA</span>
        </div>

        <div class="wm-header__tabs">
          <button class="wm-header__tab wm-header__tab--active" data-view="map" id="wm-tab-map">⊞ THE MAP</button>
          <button class="wm-header__tab" data-view="dashboard" id="wm-tab-dashboard">⊟ THE BALL</button>
        </div>

        <div class="wm-header__meta">
          <button class="wm-header__exit" id="wm-btn-exit">⏏ EXIT</button>
          <span class="wm-header__status" id="wm-signal">THREAT MATRIX: BOOTING</span>
          <span class="wm-header__social">FOLLOW WORLD MONITOR ON 𝕏</span>
          <span id="wm-timestamp">${getCurrentTimestamp()}</span>
        </div>
      </div>

      <!-- Main Body -->
      <div class="wm-body" id="wm-body">
        <!-- Left Sidebar: Category Filters -->
        <div class="wm-sidebar-left" id="wm-sidebar-left">
          ${buildFilterSidebar()}
        </div>

        <!-- Map Container -->
        <div class="wm-map-container" id="wm-map-zone">
          <div class="wm-loading">
            <div class="wm-loading__spinner"></div>
            Initializing OSINT feed...
          </div>
        </div>

        <!-- Right Side Panels -->
        <div class="wm-panels" id="wm-panels">
          <!-- Live TV Panel -->
          <div class="wm-panel" id="wm-panel-tv" style="flex: 0 0 200px;">
            <div class="wm-panel__header" data-panel="tv">
              <span class="wm-panel__title">📺 LIVE BROADCAST</span>
              <span class="wm-panel__toggle">▼</span>
            </div>
            <div class="wm-panel__body" id="wm-tv-body"></div>
          </div>

          <!-- News Wire Panel -->
          <div class="wm-panel" id="wm-panel-news" style="flex: 1.35;">
            <div class="wm-panel__header wm-panel__header--news" data-panel="news">
              <span class="wm-panel__title">🛰 LIVE NEWS WIRE</span>
              <div class="wm-panel__actions">
                <button class="wm-panel__action" id="wm-news-refresh" title="Refresh feed" aria-label="Refresh feed">⟳</button>
                <span class="wm-panel__toggle">▼</span>
              </div>
            </div>
            <div class="wm-panel__body" id="wm-news-body">
              <div class="wm-loading">
                <div class="wm-loading__spinner"></div>
                Pulling latest global wire...
              </div>
            </div>
          </div>

          <!-- DEFCON Panel -->
          <div class="wm-panel" id="wm-panel-defcon" style="flex: 1;">
            <div class="wm-panel__header" data-panel="defcon">
              <span class="wm-panel__title">☢ DEFCON STATUS</span>
              <span class="wm-panel__toggle">▼</span>
            </div>
            <div class="wm-panel__body" id="wm-defcon-body"></div>
          </div>

          <!-- Financial Panel -->
          <div class="wm-panel" id="wm-panel-finance" style="flex: 1;">
            <div class="wm-panel__header" data-panel="finance">
              <span class="wm-panel__title">💹 MARKETS</span>
              <span class="wm-panel__toggle">▼</span>
            </div>
            <div class="wm-panel__body" id="wm-finance-body"></div>
          </div>

          <!-- Prediction Markets Panel -->
          <div class="wm-panel" id="wm-panel-prediction" style="flex: 1;">
            <div class="wm-panel__header" data-panel="prediction">
              <span class="wm-panel__title">🎯 PREDICTION MARKETS</span>
              <span class="wm-panel__toggle">▼</span>
            </div>
            <div class="wm-panel__body" id="wm-prediction-body"></div>
          </div>
        </div>
      </div>

      <!-- News Ticker -->
      <div class="wm-ticker">
        <div class="wm-ticker__live">
          <span class="wm-ticker__live-dot"></span>
          LIVE
        </div>
        <div class="wm-ticker__source">OSINT</div>
        <div class="wm-ticker__scroll">
          <div class="wm-ticker__content" id="wm-ticker-content">
            ${buildNewsTicker(['GLOBAL WIRE BOOTING', 'WAITING FOR LIVE FEED'])}
          </div>
        </div>
      </div>

      <!-- CRT Scanline Overlay -->
      <div class="wm-scanlines"></div>
    </div>
  `;
}

/**
 * Initialize World Monitor page — mount all components
 */
export function initWorldMonitor(): void {
  if (isFeatureEnabled('charts_v2_engine')) {
    destroyWorldMonitor();
    const root = document.getElementById('world-monitor-v2-root');
    if (root) renderPhase2WorldMonitorEngine(root);
    return;
  }

  destroyWorldMonitor();
  currentTheme = 'all';
  currentView = 'map';
  setGDELTTheme('all');

  // Mount the GDELT Map
  const mapZone = document.getElementById('wm-map-zone');
  if (mapZone) renderGDELTMap(mapZone);
  cleanupFns.push(destroyGDELTMap);

  // Mount Live TV
  const tvBody = document.getElementById('wm-tv-body');
  if (tvBody) renderWorldLiveTV(tvBody);

  // Mount live news wire
  void loadWorldNews(true);

  // Mount DEFCON Tracker
  const defconBody = document.getElementById('wm-defcon-body');
  if (defconBody) renderDEFCONTracker(defconBody);
  cleanupFns.push(destroyDEFCONTracker);

  // Mount Financial Tracker
  const financeBody = document.getElementById('wm-finance-body');
  if (financeBody) renderFinancialTracker(financeBody);
  cleanupFns.push(destroyFinancialTracker);

  // Mount Prediction Markets
  const predictionBody = document.getElementById('wm-prediction-body');
  if (predictionBody) renderPredictionMarkets(predictionBody);
  cleanupFns.push(destroyPredictionMarkets);

  // Sidebar filter click handlers
  document.querySelectorAll('#wm-sidebar-left .wm-sidebar__btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const theme = normalizeTheme((btn as HTMLElement).dataset.theme || 'all');
      document.querySelectorAll('#wm-sidebar-left .wm-sidebar__btn').forEach(b =>
        b.classList.remove('wm-sidebar__btn--active')
      );
      btn.classList.add('wm-sidebar__btn--active');
      currentTheme = theme;
      setGDELTTheme(theme);
      const themed = pickLatestWorldHeadlines(theme, cachedNews);
      renderWorldNewsPanel(theme, themed);
      void loadWorldNews();
    });
  });

  const refreshNewsBtn = document.getElementById('wm-news-refresh');
  const onRefreshNews = (event: Event) => {
    event.stopPropagation();
    void loadWorldNews(true);
  };
  refreshNewsBtn?.addEventListener('click', onRefreshNews);
  cleanupFns.push(() => refreshNewsBtn?.removeEventListener('click', onRefreshNews));

  // Panel collapse/expand
  document.querySelectorAll('.wm-panel__header').forEach(header => {
    header.addEventListener('click', () => {
      const panel = header.closest('.wm-panel');
      if (panel) {
        panel.classList.toggle('wm-panel--collapsed');
      }
    });
  });

  // View toggle (MAP / BALL)
  const tabMap = document.getElementById('wm-tab-map');
  const tabDash = document.getElementById('wm-tab-dashboard');

  tabMap?.addEventListener('click', () => {
    if (currentView !== 'map') {
      currentView = 'map';
      tabMap.classList.add('wm-header__tab--active');
      tabDash?.classList.remove('wm-header__tab--active');
      // Show standard layout
      const body = document.getElementById('wm-body');
      if (body) body.classList.remove('wm-body--dashboard');
    }
  });

  tabDash?.addEventListener('click', () => {
    if (currentView !== 'dashboard') {
      currentView = 'dashboard';
      tabDash.classList.add('wm-header__tab--active');
      tabMap?.classList.remove('wm-header__tab--active');
      const body = document.getElementById('wm-body');
      if (body) body.classList.add('wm-body--dashboard');
    }
  });

  // Exit Button
  const btnExit = document.getElementById('wm-btn-exit');
  btnExit?.addEventListener('click', () => {
    window.location.hash = '#/';
  });

  // Live timestamp update
  const tsInterval = setInterval(() => {
    const tsEl = document.getElementById('wm-timestamp');
    if (tsEl) {
      tsEl.textContent = getCurrentTimestamp();
    } else {
      clearInterval(tsInterval);
    }
  }, 1000);
  cleanupFns.push(() => clearInterval(tsInterval));

  const newsInterval = setInterval(() => {
    void loadWorldNews();
  }, 45000);
  cleanupFns.push(() => clearInterval(newsInterval));
}

/**
 * Cleanup when leaving the page
 */
export function destroyWorldMonitor(): void {
  cleanupFns.forEach(fn => fn());
  cleanupFns.length = 0;
}
